/**
 * Color Utilities
 * 
 * Functions for generating distinct colors for statuses and other UI elements.
 */

/**
 * Color presets for status colors - a curated palette
 */
export const STATUS_COLOR_PRESETS = [
  '#6b7280', '#3b82f6', '#8b5cf6', '#ec4899',
  '#f59e0b', '#22c55e', '#06b6d4', '#ef4444',
  '#84cc16', '#f97316', '#14b8a6', '#a855f7',
  '#eab308', '#0ea5e9', '#d946ef', '#64748b',
];

/**
 * Convert hex color to HSL
 * @param {string} hex - Hex color string (e.g., '#3b82f6')
 * @returns {Object} { h, s, l } where h is 0-360, s and l are 0-100
 */
const hexToHsl = (hex) => {
  // Remove # if present
  hex = hex.replace(/^#/, '');
  
  // Parse RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  
  let h = 0;
  let s = 0;
  
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: break;
    }
  }
  
  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
};

/**
 * Convert HSL to hex color
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Lightness (0-100)
 * @returns {string} Hex color string
 */
const hslToHex = (h, s, l) => {
  s /= 100;
  l /= 100;
  
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = l - c / 2;
  
  let r = 0, g = 0, b = 0;
  
  if (h < 60) { r = c; g = x; b = 0; }
  else if (h < 120) { r = x; g = c; b = 0; }
  else if (h < 180) { r = 0; g = c; b = x; }
  else if (h < 240) { r = 0; g = x; b = c; }
  else if (h < 300) { r = x; g = 0; b = c; }
  else { r = c; g = 0; b = x; }
  
  const toHex = (n) => {
    const hex = Math.round((n + m) * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Calculate the minimum hue distance from a color to any color in a set
 * Uses circular distance since hue wraps around (0 and 360 are the same)
 * @param {number} hue - The hue to check (0-360)
 * @param {number[]} existingHues - Array of existing hues
 * @returns {number} Minimum distance (0-180)
 */
const getMinHueDistance = (hue, existingHues) => {
  if (existingHues.length === 0) return 180;
  
  return Math.min(...existingHues.map(existing => {
    const diff = Math.abs(hue - existing);
    return Math.min(diff, 360 - diff); // Circular distance
  }));
};

/**
 * Find the most distinct color from existing colors
 * Maximizes hue distance while keeping good saturation and lightness
 * @param {string[]} existingColors - Array of hex colors already in use
 * @param {string[]} presets - Optional array of preset colors to choose from
 * @returns {string} Hex color that's most distinct from existing colors
 */
export const getMostDistinctColor = (existingColors = [], presets = STATUS_COLOR_PRESETS) => {
  if (existingColors.length === 0) {
    // No existing colors - return first preset
    return presets[0];
  }
  
  // Convert existing colors to HSL and extract hues
  const existingHues = existingColors
    .filter(c => c && typeof c === 'string')
    .map(c => hexToHsl(c).h);
  
  // First, try to find the best preset color
  let bestPreset = null;
  let bestPresetDistance = -1;
  
  for (const preset of presets) {
    const hsl = hexToHsl(preset);
    const distance = getMinHueDistance(hsl.h, existingHues);
    
    // Skip if this exact color is already used
    if (existingColors.includes(preset)) continue;
    
    if (distance > bestPresetDistance) {
      bestPresetDistance = distance;
      bestPreset = preset;
    }
  }
  
  // If we found a good preset (at least 30 degrees away), use it
  if (bestPreset && bestPresetDistance >= 30) {
    return bestPreset;
  }
  
  // Otherwise, generate a new color with optimal hue
  // Find the hue that maximizes minimum distance to all existing hues
  let bestHue = 0;
  let bestDistance = -1;
  
  // Check every 10 degrees around the color wheel
  for (let h = 0; h < 360; h += 10) {
    const distance = getMinHueDistance(h, existingHues);
    if (distance > bestDistance) {
      bestDistance = distance;
      bestHue = h;
    }
  }
  
  // Generate color with good saturation (60-70%) and lightness (50-55%)
  // These values work well for UI elements on both light and dark backgrounds
  return hslToHex(bestHue, 65, 52);
};

/**
 * Get a suggested default color for a new status based on existing statuses
 * @param {Array} statuses - Existing statuses array
 * @returns {string} Suggested hex color
 */
export const getSuggestedStatusColor = (statuses = []) => {
  const existingColors = statuses
    .map(s => s.color)
    .filter(c => c && typeof c === 'string');
  
  return getMostDistinctColor(existingColors, STATUS_COLOR_PRESETS);
};
